import { Component, HostListener, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseService } from '../../services/base.service';
import { Subject, Subscription } from 'rxjs';
import { FileSystemService } from '../../services/file-system.service';
import {
  ATTACHMENTS_TYPES,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
  UPLOAD_FILE_ICONS,
} from '../../enum/constants';
import { NotificationService } from '../../services/notifications.service';
import { ChatService } from '../../services/chat.service';
import { TranslateService } from '@ngx-translate/core';
import { IError } from '../../interfaces';
import { Space } from 'src/app/modules/crm/file-system/components/file-system/file-system.interface';

@Component({
  selector: 'app-file-upload-new',
  templateUrl: './file-upload-new.component.html',
  styleUrl: './file-upload-new.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FileUploadNewComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();
  uploadingFiles: { subscription: Subscription; data: any; response: any }[] = [];
  uploadedFiles: any[] = [];
  pastedFiles!: File[];
  selectedFileIndex: number | null = null;
  isFileView: boolean = false;
  public isLoader: boolean = false;
  file: any;
  outsideFilesLength: any;
  outsideFiles: any[] = [];
  maxUploadFiles?: number;
  allowedMediaTypes?: string[];
  maxUploadSize?: number;
  isFileChoice: boolean = false;
  soloMediaTypes?: string[];
  channelType: CHANNEL_TYPE;
  isTmp: boolean = true; // tmp or jespace
  spaceDirectoryId: string;
  private uploadPath: 'temp' | 'avatar' = 'temp';
  deleteTmpFiles: boolean = true;
  sendAsFile: boolean = false;
  forceFile: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<FileUploadNewComponent>,
    private readonly translate: TranslateService,
    protected readonly fileSystemService: FileSystemService,
    public readonly baseService: BaseService,
    private readonly notificationService: NotificationService,
    public chatService: ChatService,
  ) {
    this.pastedFiles = data?.data?.files;
    this.outsideFilesLength = data?.data?.length || 0;
    this.outsideFiles = data?.data?.outside_files;
    this.maxUploadFiles = data?.data?.max_upload_files;
    this.allowedMediaTypes = data?.data?.allowed_media_types || [];
    this.maxUploadSize = data?.data?.max_upload_size;
    this.isFileChoice = data?.data?.is_file_choice;
    this.soloMediaTypes = data?.data?.solo_media_types;
    this.channelType = data?.data?.channel_type;
    this.isTmp = data?.data?.hasOwnProperty('is_tmp') ? data.data.is_tmp : true;
    this.spaceDirectoryId = data?.data?.space_directory_id;
    this.uploadPath = data?.data?.upload_path || 'temp';
  }

  ngOnInit(): void {
    if (this.pastedFiles?.length) {
      this.uploadFiles(this.pastedFiles);
    }
  }

  public ngOnDestroy() {
    if (this.deleteTmpFiles && this.isTmp) {
      this.handleDeleteTmpFiles();
    }

    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onChooseFiles(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;
    this.uploadFiles(files);
  }

  onDroppedFiles(event: FileList) {
    this.uploadFiles(event);
  }

  uploadFiles(files: any) {
    const incoming: File[] = Array.from(files);

    const accepted: File[] = [];

    let totalCount = this.uploadedFiles.length + this.uploadingFiles.length + this.outsideFilesLength;

    const maxFileSizeBytes = this.maxUploadSize && this.maxUploadSize * 1024 * 1024;

    let hasSoloMimeType =
      !!this.soloMediaTypes?.length &&
      [...this.uploadedFiles, ...this.uploadingFiles, ...this.outsideFiles].some((f) =>
        this.soloMediaTypes!.some((mt) => f.mimeType === mt),
      );

    for (const file of incoming) {
      if (this.maxUploadFiles && totalCount >= this.maxUploadFiles) {
        this.notificationService.showWarning(
          this.translate.instant('notifications_texts.warn.warn'),
          this.translate.instant('notifications_texts.warn.no_more_upload_files', { limit: this.maxUploadFiles }),
        );
        break;
      }

      if (this.allowedMediaTypes?.length && !this.allowedMediaTypes.some((mt) => file.type.match(mt))) {
        this.notificationService.showWarning(
          this.translate.instant('notifications_texts.warn.warn'),
          this.translate.instant('notifications_texts.warn.invalid_files_type'),
        );
        continue;
      }

      if (maxFileSizeBytes && file.size > maxFileSizeBytes) {
        this.notificationService.showWarning(
          this.translate.instant('notifications_texts.warn.warn'),
          this.translate.instant('notifications_texts.warn.file_too_large', { limit: this.maxUploadSize }),
        );
        continue;
      }

      if (this.soloMediaTypes?.length) {
        if (this.soloMediaTypes.some((mt) => file.type.match(mt))) {
          hasSoloMimeType = true;
        }

        if (hasSoloMimeType && totalCount + accepted.length) {
          this.notificationService.showWarning(
            this.translate.instant('notifications_texts.warn.warn'),
            this.translate.instant('notifications_texts.warn.solo_media_types', {
              types: this.soloMediaTypes.join(', '),
            }),
          );
          continue;
        }
      }

      accepted.push(file);
      totalCount++;
    }

    if (!accepted?.length) return;

    if (this.isTmp) {
      this.uploadTmpFile(accepted);
    } else {
      this.uploadFilesToSpace(accepted);
    }
  }

  filterForceFileFormats() {
    if (!this.isFileChoice) return;

    this.forceFile = false;

    let hasFileFormat = false;
    let hasAudioFormat = false;
    let hasMediaFormat = false;

    const allFiles = [...this.uploadedFiles, ...this.outsideFiles];

    for (const file of allFiles) {
      switch (file.type) {
        case ATTACHMENTS_TYPES.DOCUMENT:
          hasFileFormat = true;
          break;
        case ATTACHMENTS_TYPES.AUDIO:
          hasAudioFormat = true;
          break;
        case ATTACHMENTS_TYPES.VIDEO:
        case ATTACHMENTS_TYPES.IMAGE:
          hasMediaFormat = true;
          break;
      }
    }

    if (hasFileFormat || (hasAudioFormat && hasMediaFormat)) {
      this.forceFile = true;
      this.sendAsFile = true;
    }
  }

  uploadTmpFile(filesArray: File[]) {
    for (let i = 0; i < filesArray.length; i++) {
      const file: any = filesArray[i];

      const channelType = this.channelType ? this.channelType : CHANNEL_TYPE.UNKNOWN;

      const req = this.fileSystemService.uploadTempFile(file, channelType, this.uploadPath).subscribe({
        next: (res: any) => {
          const uploadingFile = this.uploadingFiles.find((item) => item.data === file);
          if (uploadingFile) {
            uploadingFile.response = res;
            if (res.state) {
              this.uploadedFiles.push(res.body.data);
              this.uploadingFiles = this.uploadingFiles.filter((item) => item.data !== file);

              this.filterForceFileFormats();
            }
          }
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((error: IError) => {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[error.code] || DEFAULT_ERROR,
            );
          });
        },
      });
      this.uploadingFiles.push({ subscription: req, data: file, response: {} });
    }
  }

  uploadFilesToSpace(filesArray: File[]): void {
    this.fileSystemService.getSpaceInfo().subscribe({
      next: (space: Space) => {
        let usedSpace = space.used;

        for (const file of filesArray) {
          usedSpace += file.size;
          if (usedSpace > space.total) {
            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.not_enough_space +
                file.name,
            );
            continue;
          }

          const req = this.fileSystemService.uploadFile(file, this.spaceDirectoryId).subscribe({
            next: (res: any) => {
              const uploadingFile = this.uploadingFiles.find((item) => item.data === file);
              if (uploadingFile) {
                uploadingFile.response = res;
                if (res.state) {
                  this.uploadedFiles.push(res.body.data);
                  this.uploadingFiles = this.uploadingFiles.filter((item) => item.data !== file);
                }
              }
            },
          });
          this.uploadingFiles.push({ subscription: req, data: file, response: {} });
        }
      },
    });
  }

  deleteUploadingFile(i: number) {
    if (this.uploadingFiles[i]) {
      this.uploadingFiles[i].subscription.unsubscribe();
      this.uploadingFiles.splice(i, 1);
    }
  }

  deleteUploadedFile(i: number) {
    if (this.selectedFileIndex === i) {
      this.selectedFileIndex = null;
      this.file = null;
      this.isFileView = false;
    }
    if (this.isTmp) {
      this.fileSystemService.deleteTempFile(this.uploadedFiles[i].key).subscribe();
    } else {
      this.fileSystemService.delete(this.uploadedFiles[i].id).subscribe();
    }

    this.uploadedFiles.splice(i, 1);

    if (this.isTmp) {
      this.filterForceFileFormats();
    }
  }

  onViewFile(file: any, index: number) {
    if (index === this.selectedFileIndex) {
      this.isFileView = false;
      this.isLoader = false;
      this.selectedFileIndex = null;
      this.file = null;
    } else {
      this.selectedFileIndex = index;
      this.file = file;
      this.isLoader = true;
      this.isFileView = true;
    }
  }

  fileLoaded() {
    this.isLoader = false;
  }

  onCloseModal() {
    if (!this.isTmp) {
      this.uploadedFiles = [];
      this.fileSystemService.updateDirectory();
    } else {
      if (this.isFileChoice && (this.sendAsFile || this.forceFile)) {
        this.uploadedFiles.forEach((f) => {
          f.type = ATTACHMENTS_TYPES.DOCUMENT;
        });
        this.outsideFiles?.forEach((f) => {
          f.type = ATTACHMENTS_TYPES.DOCUMENT;
        });
      }

      this.deleteTmpFiles = false;
    }

    this.dialogRef.close(this.uploadedFiles);
  }

  handleDeleteTmpFiles() {
    if (this.uploadingFiles.length) {
      this.uploadingFiles.forEach((item) => {
        item.subscription.unsubscribe();
      });
      this.uploadingFiles.splice(0, this.uploadingFiles.length);
    } else if (this.uploadedFiles.length) {
      this.uploadedFiles.forEach((item) => {
        this.fileSystemService.deleteTempFile(item.key).subscribe();
      });
      this.uploadedFiles.splice(0, this.uploadedFiles.length);
    }
  }

  onCancelModal() {
    this.dialogRef.close();
  }

  resetInput(event: any) {
    event.target.value = null;
  }

  @HostListener('window:paste', ['$event'])
  eventPaste(e: ClipboardEvent) {
    if (!e.clipboardData) return;

    const files = Array.from(e.clipboardData.items)
      .map((v: DataTransferItem) => v.getAsFile())
      .filter((v): v is File => !!v);

    if (files?.length) {
      this.uploadFiles(files);
      e.preventDefault();
    }
  }

  protected readonly UPLOAD_FILE_ICONS = UPLOAD_FILE_ICONS;
  protected readonly ATTACHMENTS_TYPES = ATTACHMENTS_TYPES;
}
